import { useEffect, useMemo, useRef, useState } from 'react';

import { Chart } from '@highcharts/react';
import { Box, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import useRegularChart from '@/components/Chart/RegularChart/useRegularChart';
import type { ExtendedOptions } from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import TimeRangeButtons from '@/components/TimeRangeButtons/TimeRangeButtons';
import type { TimeRangeButton } from '@/constants/constants';
import {
  DEFAULT_CHART_HEIGHT,
  regularChartPalette,
  TABLE_ROW_HEIGHT_PX
} from '@/constants/constants';
import useCsvDownload from '@/hooks/useCsvDownload';
import { Highcharts } from '@/lib/highcharts';
import { deepMerge } from '@/utils/deepMerge';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { downloadJsonFileFromDatalake } from '@/utils/downloadJsonFileFromDatalake';
import { queryApiById } from '@/utils/queryApi';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;

type LayoutProps = {
  fullWidth?: boolean;
  height?: number;
};

type CustomChartProps = {
  metadata?: Record<string, object | string>;
  layout?: LayoutProps;
};

const useChartConfig = (fileName: string | undefined) => {
  return useQuery({
    queryKey: [fileName, GROUP_NAME],
    queryFn: () => {
      if (fileName) return downloadJsonFileFromDatalake(fileName, GROUP_NAME);
      return Promise.resolve(null);
    },
    enabled: !!fileName,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity
  });
};

const useChartData = (fileName?: string, queryFileId?: string) => {
  const [from, setFrom] = useState<string | undefined>(undefined);
  const dataQueryResult = useQuery({
    queryKey: [fileName, GROUP_NAME, queryFileId, from],
    queryFn: () => {
      if (queryFileId) {
        return queryApiById(queryFileId, from);
      } else if (fileName) {
        return downloadJsonFileFromDatalake(fileName, GROUP_NAME);
      }
      return Promise.resolve(null);
    },
    enabled: !!fileName || !!queryFileId,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity
  });

  return { dataQueryResult, setFrom, from };
};
const Index = ({ metadata, layout }: CustomChartProps) => {
  const theme = useTheme();

  const seriesFileName = metadata?.seriesFileName as string;
  const configFileName = metadata?.configFileName as string;
  const queryFileId = metadata?.queryFileId as string;
  const downloadFileId = metadata?.downloadFileId as string;

  const timeRangeButtons = useMemo(
    () => (metadata?.timeRangeButtons as TimeRangeButton[]) || [],
    [metadata]
  );
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const init = useRef(false);

  useEffect(() => {
    if (init.current || timeRangeButtons.length < 1) return;
    const index = timeRangeButtons.findIndex(bt => bt.default);

    if (index > -1) {
      setActiveButtonIndex(index);
      setFrom(timeRangeButtons[index].value);
      init.current = true;
    }
  }, [timeRangeButtons]);
  // Ensure context ref sync
  const { height } = layout || {};

  const {
    data: chartConfig,
    isError: isChartConfigError,
    isLoading: isChartConfigLoading
  } = useChartConfig(configFileName);
  const { dataQueryResult, from, setFrom } = useChartData(seriesFileName, queryFileId);
  const {
    data: chartSeries,
    isError: isChartSeriesError,
    isLoading: isChartSeriesLoading
  } = dataQueryResult;
  useCsvDownload(from, downloadFileId);

  const { chart, handleChartRef, chartContainerRef } = useRegularChart({
    chartSeries
  });
  /** Build chart options */
  const chartOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const chartHeight = height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT;

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: regularChartPalette,
      exporting: {
        buttons: { contextButton: { enabled: false } },
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            itemStyle: {
              color: theme.palette.text.primary,
              fontSize: '0.8em',
              textOverflow: 'ellipsis'
            }
          },
          scrollbar: { enabled: false }
        }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: theme.palette.text.primary } },
      credits: { enabled: false },
      xAxis: { labels: { style: { color: theme.palette.text.primary } } },
      yAxis: {
        title: { text: '' },
        labels: { style: { color: theme.palette.text.primary } }
      },
      series: chartSeries,
      chart: {
        reflow: true,
        animation: false,
        backgroundColor: theme.palette.background.paper,
        height: chartHeight,
        style: {
          color: theme.palette.text.primary,
          fontFamily: 'Cairo, sans-serif'
        }
      }
    };

    const merged = deepMerge(baseOptions, chartConfig);

    // Handle sankey formatter safely
    if (merged.chart?.type === 'sankey') {
      if (merged.tooltip?.formatter) {
        try {
          merged.tooltip.formatter = eval(`(${merged.tooltip.formatter})`);
        } catch {
          console.warn('Invalid sankey tooltip formatter');
        }
      }
    }
    return merged;
  }, [chartConfig, chartSeries, height, theme]);
  const hasAnyData =
    Array.isArray(chartSeries) &&
    chartSeries.some(s => Array.isArray(s?.data) && s.data.length > 0);

  /** Loading & Error states */
  if (isChartSeriesLoading || isChartConfigLoading) {
    return (
      <Box sx={{ height: DEFAULT_CHART_HEIGHT }}>
        <Loader />
      </Box>
    );
  }

  /** Render chart */
  if (chartOptions) {
    return (
      <Box
        ref={chartContainerRef}
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box className='base-chart'>
          {timeRangeButtons.length > 0 && (
            <TimeRangeButtons
              activeButtonIndex={activeButtonIndex}
              setActiveButtonIndex={setActiveButtonIndex}
              timeRangeButtons={timeRangeButtons}
              onButtonClick={setFrom}
            />
          )}
          {(isChartSeriesError || isChartConfigError) && <EmptyState />}
          {hasAnyData && (
            <Chart
              ref={handleChartRef}
              highcharts={Highcharts as any}
              options={chartOptions as any}
            />
          )}
          {!hasAnyData && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '372px',
                textAlign: 'center',
                color: 'text.secondary',
                flex: 1
              }}
            >
              <Typography variant='body1'>No data available for selected time period</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

export default Index;
